
import * as React from 'react';
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import BlogBreadcrumb from '../components/BlogBreadcrumb';
import BlogMeta from '../components/BlogMeta';
import ShareButtons from '../components/ShareButtons';
import HelpfulFeedback from '../components/HelpfulFeedback';
import RelatedPosts from '../components/RelatedPosts';
import { supabase, type BlogPost } from '../../../utils/supabase';

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (slug) {
      fetchPost(slug);
    }
  }, [slug]);

  const fetchPost = async (postSlug: string) => {
    setLoading(true);
    setNotFound(false);
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', postSlug)
        .eq('is_published', true)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        setNotFound(true);
        return;
      }

      setPost(data);
      incrementViews(data.id, data.views);
      updatePageMeta(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Erro ao buscar artigo:', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const incrementViews = async (id: string, currentViews: number) => {
    try {
      await supabase
        .from('blog_posts')
        .update({ views: currentViews + 1 })
        .eq('id', id);
    } catch (error) {
      console.error('Erro ao incrementar views:', error);
    }
  };

  const updatePageMeta = (data: BlogPost) => {
    document.title = `${data.title} | Blog TG WEB`;

    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', data.excerpt);
    setMeta('og:title', data.title, 'property');
    setMeta('og:description', data.excerpt, 'property');
    setMeta('og:type', 'article', 'property');
    setMeta('og:image', data.image_url, 'property');
    setMeta('og:url', `https://tgweb.com.br/blog/${data.slug}`, 'property');
  };

  const getBlogPostingSchema = (data: BlogPost) => ({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: data.title,
    description: data.excerpt,
    image: {
      '@type': 'ImageObject',
      url: data.image_url,
      width: 1200,
      height: 630,
    },
    author: {
      '@type': 'Person',
      name: data.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'TG WEB',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/d59a2823757fedf9fab30970c772cc93/fc97616593fadb8f9287babe64f21a28.png',
        width: 200,
        height: 60,
      },
    },
    datePublished: data.published_at,
    dateModified: data.updated_at || data.published_at,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://tgweb.com.br/blog/${data.slug}`,
    },
    url: `https://tgweb.com.br/blog/${data.slug}`,
    wordCount: data.content ? data.content.split(/\s+/).length : 0,
    articleSection: data.category,
    inLanguage: 'pt-BR',
    isAccessibleForFree: true,
    thumbnailUrl: data.image_url,
    timeRequired: `PT${data.reading_time || 5}M`,
    keywords: data.category ? `${data.category}, gestão de transportadora, TG WEB, sistema de transporte` : 'gestão de transportadora, TG WEB, sistema de transporte',
    copyrightHolder: {
      '@type': 'Organization',
      name: 'TG WEB',
    },
    copyrightYear: data.published_at ? new Date(data.published_at).getFullYear() : new Date().getFullYear(),
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-loader-4-line animate-spin text-4xl text-primary mb-4 block"></i>
            <p className="text-neutral-600">Carregando artigo...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20">
          <div className="max-w-2xl mx-auto px-4 text-center py-20">
            <div className="w-20 h-20 flex items-center justify-center bg-neutral-100 rounded-full mx-auto mb-6">
              <i className="ri-article-line text-4xl text-neutral-400"></i>
            </div>
            <h1 className="text-3xl font-bold text-neutral-900 mb-4">Artigo não encontrado</h1>
            <p className="text-neutral-600 mb-8">
              O artigo que você está procurando não existe ou foi removido.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-700 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Voltar ao Blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Schema BlogPosting */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getBlogPostingSchema(post)) }}
      />

      {/* Header do Artigo */}
      <header className="pt-28 pb-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="mb-8">
            <BlogBreadcrumb postTitle={post.title} />
          </div>

          {/* Badge Categoria */}
          <div className="mb-5">
            <Link
              to={`/blog?category=${encodeURIComponent(post.category)}`}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-secondary/10 text-secondary text-sm font-semibold rounded-full hover:bg-secondary/20 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-bookmark-line text-xs"></i>
              </div>
              {post.category}
            </Link>
          </div>

          {/* Título */}
          <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-neutral-900 leading-tight mb-6">
            {post.title}
          </h1>

          {/* Metadados */}
          <BlogMeta
            author={post.author}
            publishedAt={post.published_at}
            readingTime={post.reading_time}
            views={post.views}
          />
        </div>
      </header>

      {/* Imagem Fullwidth */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="relative w-full h-72 md:h-96 lg:h-[28rem] rounded-2xl overflow-hidden shadow-card">
          <img
            src={post.image_url}
            alt={`${post.title} - TG WEB gestão transportadora`}
            title={`${post.title} - gestão de frotas`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
        </div>
      </div>

      {/* Conteúdo do Artigo */}
      <article className="pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_200px] gap-10">
            {/* Conteúdo Principal */}
            <div>
              <div
                className="blog-content prose prose-neutral max-w-none
                  [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-neutral-900 [&_h2]:mt-10 [&_h2]:mb-4
                  [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-neutral-900 [&_h3]:mt-8 [&_h3]:mb-3
                  [&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-neutral-800 [&_h4]:mt-6 [&_h4]:mb-2
                  [&_p]:text-base [&_p]:leading-relaxed [&_p]:text-neutral-700 [&_p]:mb-5
                  [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-5 [&_ul]:space-y-2
                  [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-5 [&_ol]:space-y-2
                  [&_li]:text-neutral-700 [&_li]:text-base [&_li]:leading-relaxed
                  [&_strong]:font-semibold [&_strong]:text-neutral-900
                  [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:text-primary-700
                  [&_blockquote]:border-l-4 [&_blockquote]:border-secondary [&_blockquote]:pl-5 [&_blockquote]:py-2 [&_blockquote]:my-6 [&_blockquote]:bg-secondary/5 [&_blockquote]:rounded-r-lg [&_blockquote]:italic [&_blockquote]:text-neutral-600
                  [&_img]:rounded-xl [&_img]:shadow-soft [&_img]:my-6
                  [&_table]:w-full [&_table]:border-collapse [&_table]:my-6
                  [&_th]:bg-neutral-100 [&_th]:px-4 [&_th]:py-2 [&_th]:text-left [&_th]:text-sm [&_th]:font-semibold [&_th]:text-neutral-700 [&_th]:border [&_th]:border-neutral-200
                  [&_td]:px-4 [&_td]:py-2 [&_td]:text-sm [&_td]:text-neutral-600 [&_td]:border [&_td]:border-neutral-200
                  [&_code]:bg-neutral-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:text-secondary-700
                  [&_pre]:bg-neutral-900 [&_pre]:text-neutral-100 [&_pre]:p-5 [&_pre]:rounded-xl [&_pre]:overflow-x-auto [&_pre]:my-6
                  [&_hr]:border-neutral-200 [&_hr]:my-8
                "
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Divider */}
              <hr className="border-neutral-200 my-10" />

              {/* Compartilhamento */}
              <div className="mb-10">
                <ShareButtons title={post.title} slug={post.slug} />
              </div>

              {/* Feedback */}
              <HelpfulFeedback postId={post.id} currentCount={post.helpful_count} />

              {/* Voltar ao Blog */}
              <div className="mt-10 text-center">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-neutral-100 text-neutral-700 font-medium rounded-lg hover:bg-neutral-200 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Voltar ao Blog
                </Link>
              </div>
            </div>

            {/* Sidebar Sticky */}
            <aside className="hidden lg:block">
              <div className="sticky top-28 space-y-6">
                {/* Share Vertical */}
                <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-100">
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3">Compartilhar</p>
                  <div className="flex flex-col gap-2">
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://tgweb.com.br/blog/${post.slug}`)}`}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#1877F2] text-white hover:opacity-90 transition-opacity cursor-pointer"
                    >
                      <i className="ri-facebook-fill"></i>
                    </a>
                    <a
                      href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(`https://tgweb.com.br/blog/${post.slug}`)}&text=${encodeURIComponent(post.title)}`}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="w-10 h-10 flex items-center justify-center rounded-lg bg-neutral-900 text-white hover:opacity-90 transition-opacity cursor-pointer"
                    >
                      <i className="ri-twitter-x-fill"></i>
                    </a>
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://tgweb.com.br/blog/${post.slug}`)}`}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#0A66C2] text-white hover:opacity-90 transition-opacity cursor-pointer"
                    >
                      <i className="ri-linkedin-fill"></i>
                    </a>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(post.title)}%20${encodeURIComponent(`https://tgweb.com.br/blog/${post.slug}`)}`}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#25D366] text-white hover:opacity-90 transition-opacity cursor-pointer"
                    >
                      <i className="ri-whatsapp-fill"></i>
                    </a>
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-gradient-to-br from-primary to-primary-800 rounded-xl p-5 text-white">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg mb-3">
                    <i className="ri-truck-line text-xl"></i>
                  </div>
                  <p className="text-sm font-bold mb-1">Teste o TG WEB</p>
                  <p className="text-xs text-white/80 mb-3">Gerencie sua transportadora com eficiência.</p>
                  <a
                    href="https://wa.me/5511999999999?text=Olá,%20gostaria%20de%20uma%20demonstração%20do%20TG%20WEB"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-secondary text-white text-xs font-semibold rounded-lg hover:bg-secondary-600 transition-all cursor-pointer whitespace-nowrap w-full justify-center"
                  >
                    Agendar Demo
                    <i className="ri-arrow-right-line text-xs"></i>
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      {/* Artigos Relacionados */}
      <RelatedPosts currentPostId={post.id} category={post.category} />

      {/* Internal Links Section */}
      <section className="py-12 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl font-bold text-neutral-900 mb-6 text-center">
            Conheça Nosso Sistema para Transportadora
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link
              to="/funcionalidades"
              className="group p-4 bg-white rounded-lg hover:shadow-soft transition-all border border-neutral-100 text-center"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-lg mx-auto mb-2 group-hover:bg-primary/20 transition-colors">
                <i className="ri-apps-line text-lg text-primary"></i>
              </div>
              <span className="text-sm font-medium text-neutral-900 group-hover:text-primary transition-colors">
                Funcionalidades
              </span>
            </Link>

            <Link
              to="/planos"
              className="group p-4 bg-white rounded-lg hover:shadow-soft transition-all border border-neutral-100 text-center"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-secondary/10 rounded-lg mx-auto mb-2 group-hover:bg-secondary/20 transition-colors">
                <i className="ri-price-tag-3-line text-lg text-secondary"></i>
              </div>
              <span className="text-sm font-medium text-neutral-900 group-hover:text-primary transition-colors">
                Planos
              </span>
            </Link>

            <Link
              to="/sistema-emissao-cte"
              className="group p-4 bg-white rounded-lg hover:shadow-soft transition-all border border-neutral-100 text-center"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-lg mx-auto mb-2 group-hover:bg-primary/20 transition-colors">
                <i className="ri-file-text-line text-lg text-primary"></i>
              </div>
              <span className="text-sm font-medium text-neutral-900 group-hover:text-primary transition-colors">
                Emissor CT-e
              </span>
            </Link>

            <Link
              to="/faq"
              className="group p-4 bg-white rounded-lg hover:shadow-soft transition-all border border-neutral-100 text-center"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-secondary/10 rounded-lg mx-auto mb-2 group-hover:bg-secondary/20 transition-colors">
                <i className="ri-question-answer-line text-lg text-secondary"></i>
              </div>
              <span className="text-sm font-medium text-neutral-900 group-hover:text-primary transition-colors">
                FAQ
              </span>
            </Link>
          </div>

          {/* Funcionalidades Links */}
          <div className="mt-6 pt-6 border-t border-neutral-200">
            <div className="flex flex-wrap justify-center gap-3 text-sm">
              <Link to="/funcionalidades/cte-mdfe" className="text-neutral-600 hover:text-primary transition-colors">
                Emissão de CT-e
              </Link>
              <span className="text-neutral-300">•</span>
              <Link to="/funcionalidades/financeiro" className="text-neutral-600 hover:text-primary transition-colors">
                Controle Financeiro
              </Link>
              <span className="text-neutral-300">•</span>
              <Link to="/funcionalidades/estoque" className="text-neutral-600 hover:text-primary transition-colors">
                Gestão de Estoque
              </Link>
              <span className="text-neutral-300">•</span>
              <Link to="/funcionalidades/motorista" className="text-neutral-600 hover:text-primary transition-colors">
                Motoristas
              </Link>
              <span className="text-neutral-300">•</span>
              <Link to="/funcionalidades/relatorios" className="text-neutral-600 hover:text-primary transition-colors">
                Relatórios
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-to-r from-primary to-primary-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Transforme a Gestão da Sua Transportadora
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
            Conheça o TG WEB e descubra como simplificar sua operação com tecnologia 100% na nuvem.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://wa.me/5511999999999?text=Olá,%20vim%20do%20blog%20e%20gostaria%20de%20agendar%20uma%20demonstração"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-secondary text-white font-semibold rounded-lg hover:bg-secondary-600 transition-all shadow-lg cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              Agendar Demonstração
            </a>
            <Link
              to="/planos"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
            >
              Ver Planos
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
